import { useTaskDetail, type TaskDetail } from '../../../lib/use-task-detail';
import { useAgentName, type AgentName } from '../../../lib/use-agent-name';
import { useMyAgents } from '../../../lib/my-agents';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { TASK_DETAILS_PAGE } from '../../../lib/tasks-bi';
import { taskStatusBadge } from '../../../lib/task-status';
import { useTaskLiveness } from '../../../lib/use-task-liveness';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { useTaskFiles, type TaskFile } from '../../../lib/task-files';
import { docViewerKind } from '../../../lib/doc-viewer';
import { openDocViewer } from '../../../lib/doc-viewer-presenter';
import { useCancelTask, type CancelTaskFlow } from '../../../lib/use-cancel-task';
import { statusIsCancellable } from '../../../lib/task-cancel';
import { useSentOutreaches, type SentOutreachSummary } from '../../../lib/use-sent-outreaches';
import { taskKnownAction } from '../../../lib/task-known-action';

type StatusVariant = ReturnType<typeof taskStatusBadge>['variant'];

export type { TaskFile };

interface Base {
  agent: AgentName;
  close: () => void;
}

export interface TaskDetailReadyVm extends Base {
  kind: 'ready';
  task: TaskDetail;
  title: string;
  description: string | undefined;
  agentName: string;
  /** The step in motion: the FIRST incomplete one (-1 when all are done). */
  currentStep: number;
  statusVariant: StatusVariant;
  /** A worker loop is executing on this task right now — drives the green "working" marker (vs static). */
  isLive: boolean;
  /** The primary CTA: its label, and what pressing it does. Normally the Maestro handoff, but a task
   *  whose text names a known next action (today: a browse session paused for a human) offers THAT
   *  action instead — see `lib/task-known-action.ts`. Built here so both layouts render one decision. */
  cta: { label: string; onClick: () => void };
  onResolve: () => void;
  /** The files the task produced, listed off the file-system by origin (see `lib/task-files.ts`).
   *  Empty when the task produced none — the Files section then renders nothing. */
  files: TaskFile[];
  /** Open one produced file in the in-app doc viewer. The content came WITH the list, so this is a
   *  pure presentation call — no resolve, and no chip that can turn out to be dead. */
  onOpenFile: (file: TaskFile) => void;
  /** The task is still open (`waiting` / `inProgress`), so it can be called off — see `lib/task-cancel`. */
  canCancel: boolean;
  /** The confirm-then-cancel flow, shared with the list row's overflow menu. */
  cancel: CancelTaskFlow;
  /** Raise the cancel confirm for this task. */
  onCancel: () => void;
  /** What the task's outreaches came to — who they went to and whether anyone wrote back. Undefined when
   *  the task sent none, and also when the task isn't in the loaded list (a deep link / the home
   *  pile-up); the section then renders nothing rather than claiming "no reply" for a thread it never
   *  read. See `lib/use-sent-outreaches.ts`. */
  sentOutreaches?: SentOutreachSummary[];
}

export type TaskDetailModalVm =
  | (Base & { kind: 'loading' })
  | (Base & { kind: 'error' })
  | TaskDetailReadyVm;

// Everything the task-details drill-in DOES, independent of how it looks — the read, the agent/step
// derivation, the BI, and the close / "Resolve with {agent}" actions. The desktop centered modal and
// the mobile full-screen sheet are two separate UIs over this one view model (Figma 12801-105529 wide,
// 12175-102348 narrow), so the behaviour, copy and analytics stay identical across the two surfaces.
export function useTaskDetailModal(taskId: string): TaskDetailModalVm {
  const state = useTaskDetail(taskId);
  // Read before the loading/error early-returns: a hook cannot be called conditionally, and the value is
  // a cache lookup rather than a fetch, so there is nothing to defer.
  const sentOutreaches = useSentOutreaches(taskId);
  const { t } = useTranslation();
  const agent = useAgentName();
  const openSideChat = useOpenSideChat();
  const { closeModal } = useTaskSidePanel();
  const { logPageClickBi, logModalActionBi } = useGenericBi('task-details');
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  const isLive = useTaskLiveness(taskId);
  const files = useTaskFiles(taskId);
  const cancel = useCancelTask(TASK_DETAILS_PAGE);
  useLoadBi('task-detail-modal', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  const close = () => {
    logModalActionBi({ ...TASK_DETAILS_PAGE, modal_name: 'task_detail', action: 'close' });
    closeModal();
  };

  if (state.kind !== 'ready') {
    return { kind: state.kind, agent, close };
  }

  const { task } = state;
  const agentName = task.agentId ? team.find((a) => a.id === task.agentId)?.name?.trim() || '' : '';
  const description = task.summary?.trim() || task.currentAction?.trim();
  const title = task.title?.trim() || t('tasks.untitledTask');
  const currentStep = task.steps.findIndex((step) => !step.completed);
  const statusVariant = taskStatusBadge(task.status).variant;

  const onResolve = () => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'resolve_with_maestro', task_id: task.id },
      { entity: { id: task.id, type: ENTITY_TYPE.task } },
    );
    closeModal();
    const message = `Let's resolve the task "${title}"`;
    // Continue the Maestro chat the task came from, so it resolves with the history rather than being
    // reintroduced to its own task. Absent when the origin is a routine/goal thread — see the field.
    openSideChat(
      task.resolveConversationId
        ? { kind: 'existing', conversationId: task.resolveConversationId, message }
        : { kind: 'new', message },
    );
  };

  // A task whose text names the next action offers that action instead of a trip through the Maestro to
  // be handed a link we already hold. The live view is the ONLY place a paused session can be cleared, so
  // it opens in a new tab rather than replacing the task the user is reading.
  const knownAction = taskKnownAction(task);
  const onGoToBrowseSession = (url: string) => () => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'go_to_browsing_session', task_id: task.id },
      { entity: { id: task.id, type: ENTITY_TYPE.task } },
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const cta = knownAction
    ? { label: t('tasks.detail.openBrowser'), onClick: onGoToBrowseSession(knownAction.url) }
    : { label: t('tasks.detail.resolveWith', { agent: agent.short }), onClick: onResolve };

  // Present the root-level doc viewer on the body the list already carries. An upload's bytes live at
  // `referenceUrl` with an empty `content`; the viewer's "content that is a bare URL" convention is what
  // both shapes collapse into, so it stays one code path.
  const onOpenFile = (file: TaskFile) => {
    logPageClickBi(
      { ...TASK_DETAILS_PAGE, button_name: 'open_file', task_id: task.id },
      {
        entity: { id: task.id, type: ENTITY_TYPE.task },
        additionalFields: { filename: file.name },
      },
    );
    const content = file.content || file.referenceUrl || '';
    openDocViewer({
      file: { name: file.name, isApp: false, content },
      kind: docViewerKind(file.name, content || undefined),
      pageName: 'document_viewer',
    });
  };

  return {
    kind: 'ready',
    agent,
    close,
    cta,
    onResolve,
    task,
    title,
    description,
    agentName,
    currentStep,
    statusVariant,
    isLive,
    files,
    onOpenFile,
    canCancel: statusIsCancellable(task.status),
    cancel,
    onCancel: () => cancel.request({ id: task.id, title }),
    sentOutreaches,
  };
}
